import { PlaygroundType } from "@/services/api/widget/types";
import {
  downloadDelimited,
  type ExportFormat,
  type ExportMode,
} from "./downloadCSV";

// ============================================================================
// Types
// ============================================================================

export interface ExportContext {
  selectedAssets: string[];
  assetDisplayNames: Record<string, string>;
  assetStats?: Record<string, { x: number; y: number; name?: string }>;
  chartSettings: {
    displayMode?: string;
    xLabel?: string;
    yLabel?: string;
  };
}

interface RawApiResponseItem {
  cols: unknown[];
  // API returns [category, long, short, net] - we use net value (index 3) for consistency with chart
  rows: Array<[string, number, number?, number?]>;
  statistics: unknown;
}

type RawApiResponse = Record<string, RawApiResponseItem>;

/**
 * Get the net exposure value from a row.
 * Row format: [category, long, short?, net?]
 * Uses net value (index 3) if available, otherwise falls back to long (index 1)
 * This matches the chart adapter logic for consistency.
 */
function getNetValue(row: [string, number, number?, number?]): number {
  // Use net value (index 3) if available, otherwise use long value (index 1)
  return row.length >= 4 && row[3] !== undefined ? row[3] : row[1];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display name for a resource ID, handling prefixed IDs like "portfolio:port-xxx"
 * Matches the logic in the exposure adapter.
 */
export function getDisplayName(
  resourceId: string,
  displayNames: Record<string, string>
): string {
  // Extract the ID part without the resource type prefix for display name lookup
  // e.g., "portfolio:port-123" becomes "port-123"
  const idParts = resourceId.split(":");
  const childId = idParts.length > 1 ? idParts[1] : resourceId;

  // First try to find display name using the child ID, then fall back to full resource ID
  return displayNames[childId] || displayNames[resourceId] || resourceId;
}

/**
 * Check if an object is a raw API response with portfolio/resource keys
 */
function isRawApiResponse(obj: unknown): obj is RawApiResponse {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return false;
  }
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;

  // Check if the first key has the expected structure with rows array
  const firstValue = (obj as Record<string, unknown>)[keys[0]];
  return (
    typeof firstValue === "object" &&
    firstValue !== null &&
    "rows" in firstValue &&
    Array.isArray((firstValue as { rows: unknown }).rows)
  );
}

/**
 * Check if data is empty (handles both array and object formats)
 */
function isDataEmpty(data: unknown): boolean {
  return (
    data === null ||
    data === undefined ||
    (Array.isArray(data) && data.length === 0) ||
    (typeof data === "object" &&
      !Array.isArray(data) &&
      Object.keys(data as object).length === 0)
  );
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Export timeseries data (Performance History, Rolling Stats)
 */
export function exportTimeseries(
  data: Record<string, unknown>[],
  context: ExportContext,
  format: ExportFormat,
  playgroundType: PlaygroundType
): void {
  const { selectedAssets, assetDisplayNames, chartSettings } = context;

  const cols = [
    { id: "date", label: "Date" },
    ...selectedAssets.map((assetId) => ({
      id: assetId,
      label: assetDisplayNames[assetId] || assetId,
    })),
  ];

  // Transform data to match chart display
  const transformedRows = data.map((point) => {
    const row: Record<string, unknown> = { date: point.date };
    selectedAssets.forEach((asset) => {
      let value: number | null =
        typeof point[asset] === "number" ? (point[asset] as number) : null;

      // Handle prefixed asset keys
      if (value === null && typeof asset === "string" && asset.includes("-")) {
        const matchingKey = Object.keys(point).find((key) =>
          key.endsWith(asset)
        );
        if (matchingKey && typeof point[matchingKey] === "number") {
          value = point[matchingKey] as number;
        }
      }
      row[asset] = typeof value === "number" && !isNaN(value) ? value : null;
    });
    return row;
  });

  const mode: ExportMode =
    playgroundType === PlaygroundType.RollingStats ||
    playgroundType === PlaygroundType.MetricsOverTime
      ? "rolling"
      : chartSettings.displayMode === "performance"
        ? "performance"
        : "index";

  const extension = format === "csv" ? "csv" : "tsv";
  downloadDelimited(transformedRows, cols, `chart-data.${extension}`, {
    mode,
    format,
  });
}

/**
 * Export scatter chart data
 */
export function exportScatterChart(
  context: ExportContext,
  format: ExportFormat
): void {
  const { selectedAssets, assetDisplayNames, assetStats, chartSettings } =
    context;

  if (!assetStats || Object.keys(assetStats).length === 0) {
    console.error("No asset stats available for scatter chart export.");
    return;
  }

  const xLabel = chartSettings.xLabel || "X Value";
  const yLabel = chartSettings.yLabel || "Y Value";

  const cols = [
    { id: "name", label: "Asset Name" },
    { id: "x", label: xLabel },
    { id: "y", label: yLabel },
  ];

  const rows = selectedAssets.map((assetId) => {
    const stats = assetStats[assetId] || { x: 0, y: 0 };
    return {
      name: assetDisplayNames[assetId] || stats.name || assetId,
      x: stats.x,
      y: stats.y,
    };
  });

  const extension = format === "csv" ? "csv" : "tsv";
  downloadDelimited(rows, cols, `scatter-data.${extension}`, {
    mode: "scatter",
    format,
  });
}

/**
 * Export raw API response data (internal helper for exposure)
 * Includes both raw values and scaled (chart) values for consistency with pie chart display
 */
function exportRawApiResponse(
  rawData: RawApiResponse,
  displayNames: Record<string, string>,
  format: ExportFormat
): void {
  const portfolioKeys = Object.keys(rawData);
  const extension = format === "csv" ? "csv" : "tsv";

  if (portfolioKeys.length === 1) {
    // Single resource - export as simple category/value table with both raw and scaled values
    const resourceData = rawData[portfolioKeys[0]];

    // Calculate total for scaling (pie chart normalizes to 100%)
    // Use net value for consistency with chart adapter
    const total = resourceData.rows.reduce(
      (sum, row) => sum + getNetValue(row),
      0
    );

    const cols = [
      { id: "category", label: "Category" },
      { id: "rawValue", label: "Unscaled Raw Value (%)" },
      { id: "chartValue", label: "Scaled Value (%)" },
    ];

    const rows = resourceData.rows.map((row) => {
      const category = row[0];
      const value = getNetValue(row);
      return {
        category,
        rawValue: value * 100, // Convert to percentage (actual portfolio allocation)
        chartValue: total > 0 ? (value / total) * 100 : 0, // Scaled to 100% (as shown in pie chart)
      };
    });

    downloadDelimited(rows, cols, `exposure-data.${extension}`, {
      mode: "exposure",
      format,
    });
  } else {
    // Multiple resources - export as resource vs categories table
    const allCategories = new Set<string>();
    portfolioKeys.forEach((key) => {
      rawData[key]?.rows?.forEach((row) => {
        allCategories.add(row[0]);
      });
    });

    const sortedCategories = Array.from(allCategories).sort();

    const cols = [
      { id: "resource", label: "Resource" },
      ...sortedCategories.map((cat) => ({ id: cat, label: cat })),
    ];

    const rows = portfolioKeys.map((portfolioKey) => {
      const resourceData = rawData[portfolioKey];
      const row: Record<string, unknown> = {
        resource: getDisplayName(portfolioKey, displayNames),
      };

      // Initialize all categories to 0
      sortedCategories.forEach((cat) => {
        row[cat] = 0;
      });

      // Fill in values from rows using net value for consistency
      resourceData?.rows?.forEach((apiRow) => {
        const category = apiRow[0];
        const value = getNetValue(apiRow);
        row[category] = value * 100; // Convert to percentage
      });

      return row;
    });

    downloadDelimited(rows, cols, `exposure-data.${extension}`, {
      mode: "exposure",
      format,
    });
  }
}

/**
 * Export exposure chart data (handles Pie, Stacked Bar, and raw API response formats)
 */
export function exportExposure(
  data: unknown,
  context: ExportContext,
  format: ExportFormat
): void {
  const { assetDisplayNames } = context;
  const extension = format === "csv" ? "csv" : "tsv";

  // Check if data is directly the raw API response (object with portfolio keys)
  if (isRawApiResponse(data)) {
    exportRawApiResponse(data, assetDisplayNames, format);
    return;
  }

  // Check if data is an array
  if (Array.isArray(data) && data.length > 0) {
    const firstItem = data[0];

    // Check if the array contains the raw API response as its first element
    if (isRawApiResponse(firstItem)) {
      exportRawApiResponse(firstItem, assetDisplayNames, format);
      return;
    }

    if (firstItem && "label" in firstItem) {
      // Pie chart format: [{ label, value, color? }, ...]
      // Include both raw values and scaled (chart) values for consistency with pie chart display
      const cols = [
        { id: "label", label: "Category" },
        { id: "rawValue", label: "Unscaled Raw Value (%)" },
        { id: "chartValue", label: "Scaled Value (%)" },
      ];

      // Calculate total for scaling (pie chart normalizes to 100%)
      const total = (
        data as { label: string; value: number; color?: string }[]
      ).reduce((sum, item) => sum + item.value, 0);

      // Values are raw decimals, multiply by 100 for percentage
      const rows = data.map(
        (item: { label: string; value: number; color?: string }) => ({
          label: item.label,
          rawValue: item.value * 100, // Actual portfolio allocation
          chartValue: total > 0 ? (item.value / total) * 100 : 0, // Scaled to 100% (as shown in pie chart)
        })
      );

      downloadDelimited(rows, cols, `exposure-data.${extension}`, {
        mode: "exposure",
        format,
      });
      return;
    }

    if (firstItem && "resource" in firstItem) {
      // Stacked bar format: [{ resource, Category1, Category2, ... }, ...]
      const categoryKeys = new Set<string>();
      data.forEach((item: Record<string, unknown>) => {
        Object.keys(item).forEach((key) => {
          if (key !== "resource") {
            categoryKeys.add(key);
          }
        });
      });

      const cols = [
        { id: "resource", label: "Resource" },
        ...Array.from(categoryKeys).map((key) => ({
          id: key,
          label: key,
        })),
      ];

      downloadDelimited(
        data as Record<string, unknown>[],
        cols,
        `exposure-data.${extension}`,
        {
          mode: "exposure",
          format,
        }
      );
      return;
    }
  }

  console.error("Unknown exposure data format:", data);
}

// ============================================================================
// Lookthrough Types
// ============================================================================

interface LookthroughHolding {
  name: string;
  weight: number;
}

interface LookthroughSubCategory {
  sub_category_name: string;
  sub_category_weight: number;
  holdings: LookthroughHolding[];
  holdings_count: number;
}

interface LookthroughCategory {
  category_name: string;
  category_weight: number;
  sub_categories: LookthroughSubCategory[];
}

interface LookthroughResourceData {
  resource_key: string;
  categories: LookthroughCategory[];
  total_coverage: number;
  unmapped_holdings: LookthroughHolding[];
  unmapped_weight: number;
}

type LookthroughResponse = Record<string, LookthroughResourceData>;

/**
 * Check if data is a Lookthrough API response
 */
function isLookthroughResponse(data: unknown): data is LookthroughResponse {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return false;
  }
  const keys = Object.keys(data);
  if (keys.length === 0) return false;

  const firstValue = (data as Record<string, unknown>)[keys[0]];
  return (
    typeof firstValue === "object" &&
    firstValue !== null &&
    "categories" in firstValue &&
    Array.isArray((firstValue as { categories: unknown }).categories)
  );
}

/**
 * Export lookthrough data as flattened rows
 * Columns: Resource | Category | Sub-Category | Holding | Weight (%)
 *
 * @param data - Raw lookthrough API response or array containing it
 * @param context - Export context with display names
 * @param format - Export format (csv or tsv)
 */
export function exportLookthrough(
  data: unknown,
  context: ExportContext,
  format: ExportFormat
): void {
  const { assetDisplayNames } = context;
  const extension = format === "csv" ? "csv" : "tsv";

  // Handle data wrapped in array (common pattern from PlaygroundContext)
  let lookthroughData: LookthroughResponse;
  if (Array.isArray(data) && data.length > 0 && isLookthroughResponse(data[0])) {
    lookthroughData = data[0];
  } else if (isLookthroughResponse(data)) {
    lookthroughData = data;
  } else {
    console.error("Unknown lookthrough data format:", data);
    return;
  }

  const cols = [
    { id: "resource", label: "Resource" },
    { id: "category", label: "Category" },
    { id: "subCategory", label: "Sub-Category" },
    { id: "holding", label: "Holding" },
    { id: "weight", label: "Weight (%)" },
  ];

  const rows: Array<{
    resource: string;
    category: string;
    subCategory: string;
    holding: string;
    weight: number;
  }> = [];

  // Flatten the hierarchical structure into rows
  for (const [resourceKey, resourceData] of Object.entries(lookthroughData)) {
    const resourceName = getDisplayName(resourceKey, assetDisplayNames);

    // Process categories and their holdings
    for (const category of resourceData.categories) {
      for (const subCategory of category.sub_categories) {
        for (const holding of subCategory.holdings) {
          rows.push({
            resource: resourceName,
            category: category.category_name,
            subCategory: subCategory.sub_category_name,
            holding: holding.name,
            weight: holding.weight * 100,
          });
        }
      }
    }

    // Process unmapped holdings
    for (const holding of resourceData.unmapped_holdings) {
      rows.push({
        resource: resourceName,
        category: "Unmapped",
        subCategory: "Unmapped",
        holding: holding.name,
        weight: holding.weight * 100,
      });
    }
  }

  downloadDelimited(rows, cols, `lookthrough-data.${extension}`, {
    mode: "exposure",
    format,
  });
}

/**
 * Export exposure-over-time data (date x categories)
 */
export function exportExposureOverTime(
  data: Record<string, unknown>[],
  format: ExportFormat
): void {
  if (!Array.isArray(data) || data.length === 0) {
    return;
  }

  const categories = Object.keys(data[0]).filter((key) => key !== "date");
  const cols = [
    { id: "date", label: "Date" },
    ...categories.map((category) => ({ id: category, label: category })),
  ];

  const rows = data.map((point) => {
    const row: Record<string, string | number | null> = {
      date: point.date instanceof Date
        ? point.date.toISOString().slice(0, 10)
        : String(point.date),
    };
    categories.forEach((category) => {
      const value = point[category];
      row[category] =
        value === null || value === undefined ? null : (value as number);
    });
    return row;
  });

  const extension = format === "tsv" ? "tsv" : "csv";
  downloadDelimited(rows, cols, `exposure-over-time.${extension}`, {
    mode: "exposure",
    format,
  });
}

/**
 * Main export handler - routes to appropriate export function based on playground type
 */
export function handleChartExport(
  playgroundType: PlaygroundType,
  data: unknown,
  context: ExportContext,
  format: ExportFormat
): void {
  if (isDataEmpty(data)) {
    console.error("Data is empty.");
    return;
  }

  switch (playgroundType) {
    case PlaygroundType.PerformanceHistory:
    case PlaygroundType.RollingStats:
    case PlaygroundType.MetricsOverTime:
    case PlaygroundType.Underwater:
      exportTimeseries(
        data as Record<string, unknown>[],
        context,
        format,
        playgroundType
      );
      break;

    case PlaygroundType.ScatterChart:
      exportScatterChart(context, format);
      break;

    case PlaygroundType.Exposure:
    case PlaygroundType.ExposureColumns:
      exportExposure(data, context, format);
      break;

    case PlaygroundType.ExposureOverTime:
      exportExposureOverTime(data as Record<string, unknown>[], format);
      break;

    case PlaygroundType.Lookthrough:
      exportLookthrough(data, context, format);
      break;

    default:
      console.error("Unsupported playground type for export:", playgroundType);
  }
}
